interface DataPoint {
  label: string;
  value: number;
}

interface SimpleLineChartProps {
  data: DataPoint[];
  title?: string;
  color?: string;
  height?: number;
  showDots?: boolean;
  showArea?: boolean;
}

export default function SimpleLineChart({
  data,
  title,
  color = '#D4AF37',
  height = 200,
  showDots = true,
  showArea = true,
}: SimpleLineChartProps) {
  if (data.length < 2) {
    return (
      <div className="text-center py-8 text-gray-500">
        Not enough data to display chart
      </div>
    );
  }

  const maxValue = Math.max(...data.map((d) => d.value), 1);
  const padding = 20;
  const chartWidth = 400;
  const chartHeight = height - 40;

  // Calculate points
  const points = data.map((item, index) => {
    const x = padding + (index / (data.length - 1)) * (chartWidth - 2 * padding);
    const y = chartHeight - padding - ((item.value / maxValue) * (chartHeight - 2 * padding));
    return { x, y, ...item };
  });

  // Create SVG path
  const linePath = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');

  // Create area path
  const areaPath = `
    ${linePath}
    L ${points[points.length - 1].x} ${chartHeight - padding}
    L ${points[0].x} ${chartHeight - padding}
    Z
  `;

  return (
    <div>
      {title && (
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      )}
      <svg width="100%" height={height} viewBox={`0 0 ${chartWidth} ${height}`} preserveAspectRatio="xMidYMid meet">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => {
          const y = chartHeight - padding - ratio * (chartHeight - 2 * padding);
          return (
            <line
              key={index}
              x1={padding}
              y1={y}
              x2={chartWidth - padding}
              y2={y}
              stroke="#E5E7EB"
              strokeWidth="1"
              strokeDasharray="4"
            />
          );
        })}

        {/* Area */}
        {showArea && (
          <path
            d={areaPath}
            fill={color}
            fillOpacity="0.1"
          />
        )}

        {/* Line */}
        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Dots */}
        {showDots && points.map((point, index) => (
          <g key={index}>
            <circle
              cx={point.x}
              cy={point.y}
              r="4"
              fill={color}
              className="cursor-pointer"
            />
            <title>{`${point.label}: ${point.value}`}</title>
          </g>
        ))}

        {/* X-axis labels */}
        {points.map((point, index) => (
          <text
            key={index}
            x={point.x}
            y={height - 8}
            textAnchor="middle"
            className="fill-gray-500 text-xs"
            fontSize="10"
          >
            {point.label}
          </text>
        ))}
      </svg>
    </div>
  );
}
